import { useCallback, useMemo, useState } from "react";

import type { Note, UUID } from "@/core/types";

export function useNotesSelection(filteredNotes: Note[] | undefined) {
  const [selectedIds, setSelectedIds] = useState<Set<UUID>>(new Set());

  // Derives visible selection by intersecting with current filtered notes,
  // replacing the useEffect-based sync in the previous implementation
  const visibleSelectedIds = useMemo(() => {
    const visibleIds = new Set(filteredNotes?.map((n) => n.id));

    return new Set([...selectedIds].filter((id) => visibleIds.has(id)));
  }, [filteredNotes, selectedIds]);

  const isAllSelected =
    (filteredNotes?.length ?? 0) > 0 && visibleSelectedIds.size === filteredNotes?.length;

  const toggleNote = useCallback((id: UUID) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds(isAllSelected ? new Set() : new Set(filteredNotes?.map((n) => n.id)));
  }, [filteredNotes, isAllSelected]);

  return { isAllSelected, toggleAll, toggleNote, visibleSelectedIds };
}
